import {
  fetchDataWithLoadMore,
  addDocToCollection,
  fetchAllData,
  updateDocument,
  fetchDocumentById,
} from "./helperAction";
import firebase from "config/firebase";
import { toast } from "react-toastify";

const db = firebase.firestore();

export const fetchAllContests = ({ schoolId, lastVisible = null }) => {
  return (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        let queryOptions = [
          { method: "orderBy", args: ["createdAt", "desc"] },
          { method: "where", args: ["contestType", "==", "Sport Contest"] },
          {
            method: "where",
            args: [
              "status",
              "in",
              ["accepted", "admin action pending", "completed", "rewarded"],
            ],
          },
        ];

        const response = await fetchDataWithLoadMore({
          collectionName: "contest",
          queryOptions,
          lastVisible,
        });

        const updatedContests = await Promise.all(
          response?.data?.map(async (contest) => {
            const contestId = contest?.id;

            const contestSchoolDoc = await db
              .collection("contest_schools")
              .where("contestId", "==", contestId)
              .where("schoolId", "==", schoolId)
              .get();

            let schoolStatus = null;
            if (!contestSchoolDoc.empty) {
              const doc = contestSchoolDoc.docs[0];
              schoolStatus = doc.data().status;
            }

            return {
              ...contest,
              schoolStatus,
            };
          })
        );
        dispatch({
          type: "FETCH_CONTESTS_SUCCESS",
          payload: {
            ...response,
            data: updatedContests,
          },
        });

        resolve({ data: updatedContests, ...response });
      } catch (error) {
        toast.error(error.message || "Unable to fetch organizers");
        reject(error);
      }
    });
  };
};

export const fetchSchoolContests = ({
  schoolId,
  status,
  lastVisible = null,
}) => {
  return (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        let queryOptions = [
          { method: "orderBy", args: ["createdAt", "desc"] },
          { method: "where", args: ["schoolId", "==", schoolId] },
          { method: "where", args: ["status", "in", status] },
        ];

        const response = await fetchDataWithLoadMore({
          collectionName: "contest_schools",
          queryOptions,
          lastVisible,
        });

        const updatedContests = await Promise.all(
          response?.data?.map(async (contest) => {
            const contestSchoolDoc = await db
              .collection("contest")
              .doc(contest?.contestId)
              .get();

            if (!contestSchoolDoc.exists) {
              return null;
            }
            return {
              id: contestSchoolDoc.id,
              ...contestSchoolDoc.data(),
            };
          })
        );

        const filteredContests = updatedContests.filter(
          (contest) => contest !== null
        );
        dispatch({
          type: "FETCH_CONTESTS_SUCCESS",
          payload: {
            ...response,
            data: filteredContests,
          },
        });

        resolve({ data: filteredContests, ...response });
      } catch (error) {
        toast.error(error.message || "Unable to fetch organizers");
        reject(error);
      }
    });
  };
};

export const participateContest = ({ payload, onSuccess = () => {} }) => {
  return (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await dispatch(
          addDocToCollection({
            collectionName: "contest_schools",
            payload,
          })
        );

        toast.success("Participated in contest successfully!");
        dispatch({
          type: "PARTICIPATE_CONTEST_SUCCESS",
          payload: payload?.contestId,
        });

        onSuccess();
        resolve(response);
      } catch (error) {
        toast.error(error.message || "Unable to add contest");
        reject(error);
      }
    });
  };
};

export const fetchContestAthletes = ({ contestId, schoolId }) => {
  return (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        let queryOptions = [
          { method: "orderBy", args: ["createdAt", "desc"] },
          { method: "where", args: ["schoolId", "==", schoolId] },
          { method: "where", args: ["contestId", "==", contestId] },
        ];

        const response = await fetchAllData({
          collectionName: "contest_athletes",
          queryOptions,
        });

        dispatch({
          type: "FETCH_CONTEST_ATHLETES",
          payload: response,
        });

        resolve(response);
      } catch (error) {
        toast.error(error.message || "Unable to fetch organizers");
        reject(error);
      }
    });
  };
};

export const updateAthletesStatus = ({ updatedAthletesStatus }) => {
  return (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        for (let athlete of updatedAthletesStatus) {
          await updateDocument({
            collectionName: "contest_athletes",
            docId: athlete?.docId,
            updateData: { isSuggested: true },
          });
        }

        toast.success("Roaster updated successfully!");
        // dispatch({
        //   type: "UPDATE_ATHLETES_STATUS_SUCCESS",
        //   payload: updatedAthletesStatus,
        // });

        resolve(updatedAthletesStatus);
      } catch (error) {
        toast.error(error.message || "Unable to update athlete statuses");
        reject(error);
      }
    });
  };
};

export const fetchContestOrganizer = ({ contestId }) => {
  return (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const contest = await fetchDocumentById({
          collectionName: "contest",
          docId: contestId,
        });

        const organizer = await fetchDocumentById({
          collectionName: "users",
          docId: contest?.userId,
        });

        dispatch({
          type: "FETCH_CONTESTS_ORGANIZER_SUCCESS",
          payload: organizer,
        });
        resolve(organizer);
      } catch (error) {
        toast.error(error.message || "Unable to fetch contest organizer");
        reject(error);
      }
    });
  };
};

export const fetchContestDetails = ({
  collectionName,
  contestId,
  contestKey = "contestId",
  userKey = "userId",
  lastVisible = null,
}) => {
  return (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        let queryOptions = [
          { method: "orderBy", args: ["createdAt", "desc"] },
          { method: "where", args: [contestKey, "==", contestId] },
        ];

        let response = await fetchDataWithLoadMore({
          collectionName,
          queryOptions,
          lastVisible,
        });

        const updatedData = await Promise.all(
          response.data.map(async (item) => {
            if (!item[userKey]) return item;
            const userDoc = await fetchDocumentById({
              collectionName: "users",
              docId: item[userKey],
            });
            return {
              ...item,
              userData: userDoc,
            };
          })
        );

        response = { ...response, data: updatedData };
        dispatch({
          type: "FETCH_CONTESTS_DETAILS_SUCCESS",
          payload: response,
        });
        resolve(response);
      } catch (error) {
        toast.error(error.message || "Unable to fetch contests");
        reject(error);
      }
    });
  };
};
